import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const ReportErrorDialog = () => {
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke("submit-error-report", {
        body: {
          email,
          description,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
        },
      });

      if (error) throw error;

      toast.success("Felrapport skickad! Vi återkommer så snart som möjligt.");
      setIsOpen(false);
      setEmail("");
      setDescription("");
    } catch (error) {
      console.error("Error submitting report:", error);
      toast.error("Kunde inte skicka felrapporten. Försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="group text-sm text-muted-foreground hover:bg-amber-300 hover:text-black hover:border-amber-300 transition-all rounded-md px-3 py-2 font-normal"
        >
          <AlertTriangle className="w-4 h-4 text-amber-500 group-hover:text-red-500 transition-colors" />
          Rapportera fel
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Rapportera fel
          </DialogTitle>
          <DialogDescription>
            Hittat något som inte fungerar som det ska? Berätta för oss så åtgärdar vi det så snart som möjligt.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Din e-post</Label>
            <Input
              id="email"
              type="email"
              placeholder="din@email.se"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Beskrivning av felet</Label>
            <Textarea
              id="description"
              placeholder="Beskriv vad som gick fel och var på sidan det hände..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={5}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Avbryt
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Skickar..." : "Skicka rapport"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
